import React, { useEffect } from 'react';
import MetricCard from '../Common/MetricCard';
import ComboChart from '../Charts/ComboChart';
import RadioTable from '../Tables/RadioTable';
import TelevisionTable from '../Tables/TelevisionTable';
import SportSignageTable from '../Tables/SportSignageTable';
import MapView from '../Maps/MapView';
import HelpIcon from '../Common/HelpIcon';
import { SkeletonChart, SkeletonTable } from '../Common/Skeletons';
import { AllReportData, CHANNEL_COLORS } from '../../types';
import { hasMetricData, hasChartData, hasTableData } from '../../utils/dataValidation';

interface ReportsViewProps {
  reportData: AllReportData;
  dealerInfo: any;
  dealerLocations: any[];
  selectedRadius: number;
  onHelpClick: (topic: string) => void;
  isLoading?: boolean;
  mlbSocialData?: { views: number; engagement: number } | null;
}

const ReportsView: React.FC<ReportsViewProps> = ({
  reportData,
  dealerInfo,
  dealerLocations,
  selectedRadius,
  onHelpClick,
  isLoading = false,
  mlbSocialData
}) => {
  // Debug logging
  useEffect(() => {
    console.log('ReportsView: Map data being passed:', reportData.map);
    console.log('ReportsView: Dealer locations:', dealerLocations);
    console.log('ReportsView: Selected radius:', selectedRadius);
  }, [reportData.map, dealerLocations, selectedRadius]);

  return (
    <div className="reports-view">
      <div className="container">
        {/* Metrics Section */}
        <section className="metrics-section">
          <div className="metrics-grid">
            {hasMetricData(reportData.summary.display) && (
              <MetricCard
                title="Display"
                value={reportData.summary.display}
                color={CHANNEL_COLORS.display}
                onHelpClick={() => onHelpClick('display')}
              />
            )}
            {hasMetricData(reportData.summary.dooh) && (
              <MetricCard
                title="DOOH"
                value={reportData.summary.dooh}
                color={CHANNEL_COLORS.dooh}
                onHelpClick={() => onHelpClick('dooh')}
                hideClicks={true}
              />
            )}
            {hasMetricData(reportData.summary.streaming_tv) && (
              <MetricCard
                title="Streaming TV"
                value={reportData.summary.streaming_tv}
                color={CHANNEL_COLORS.streaming_tv}
                onHelpClick={() => onHelpClick('streaming_tv')}
                hideClicks={true}
              />
            )}
            {hasMetricData(reportData.summary.broadcast_tv) && (
              <MetricCard
                title="Broadcast TV"
                value={reportData.summary.broadcast_tv}
                color={CHANNEL_COLORS.broadcast_tv}
                onHelpClick={() => onHelpClick('broadcast_tv')}
                hideClicks={true}
              />
            )}
            {hasMetricData(reportData.summary.sport_signage) && (
              <MetricCard
                title="Sport Signage – MLB & NCAA"
                value={reportData.summary.sport_signage}
                color={CHANNEL_COLORS.sport_signage}
                onHelpClick={() => onHelpClick('sport_signage')}
                hideClicks={true}
              />
            )}
            {hasMetricData(reportData.summary.radio) && (
              <MetricCard
                title="Radio"
                value={reportData.summary.radio}
                color={CHANNEL_COLORS.radio}
                onHelpClick={() => onHelpClick('radio')}
                hideClicks={true}
              />
            )}
            {hasMetricData(reportData.summary.streaming_audio) && (
              <MetricCard
                title="Streaming Audio"
                value={reportData.summary.streaming_audio}
                color={CHANNEL_COLORS.streaming_audio}
                onHelpClick={() => onHelpClick('streaming_audio')}
                hideClicks={true}
              />
            )}
            {hasMetricData(reportData.summary.youtube) && (
              <MetricCard
                title="YouTube"
                value={reportData.summary.youtube}
                color={CHANNEL_COLORS.youtube}
                onHelpClick={() => onHelpClick('youtube')}
              />
            )}
            {hasMetricData(reportData.summary.social) && (
              <MetricCard
                title="Social"
                value={reportData.summary.social}
                color={CHANNEL_COLORS.social}
                onHelpClick={() => onHelpClick('social')}
              />
            )}
            {hasMetricData(reportData.summary.paid_social) && (
              <MetricCard
                title="Paid Social"
                value={reportData.summary.paid_social}
                color={CHANNEL_COLORS.paid_social}
                onHelpClick={() => onHelpClick('paid_social')}
              />
            )}
            {hasMetricData(reportData.summary.store_visits) && (
              <MetricCard
                title="Store Visits"
                value={reportData.summary.store_visits}
                color={CHANNEL_COLORS.store_visits}
                onHelpClick={() => onHelpClick('store_visits')}
              />
            )}
            {mlbSocialData && mlbSocialData.views > 0 && (
              <MetricCard
                title="MLB National Social – Views"
                value={mlbSocialData.views}
                color="#1877F2"
                hideClicks={true}
                onHelpClick={() => onHelpClick('mlb_social_views')}
              />
            )}
            {mlbSocialData && mlbSocialData.engagement > 0 && (
              <MetricCard
                title="MLB National Social – Engagement"
                value={mlbSocialData.engagement}
                color="#1DA1F2"
                hideClicks={true}
                onHelpClick={() => onHelpClick('mlb_social_engagement')}
              />
            )}
          </div>
        </section>

        {/* Charts Section */}
        <section className="charts-section">
          <div className="charts-grid">
            {isLoading ? (
              <>
                <SkeletonChart height={300} />
                <SkeletonChart height={300} />
                <SkeletonChart height={300} />
                <SkeletonChart height={300} />
                <SkeletonChart height={300} />
                <SkeletonChart height={300} />
                <SkeletonChart height={300} />
              </>
            ) : (
              <>
                {hasChartData(reportData.display_chart) && (
                  <ComboChart
                    data={reportData.display_chart}
                    title="Display Performance"
                    color={CHANNEL_COLORS.display}
                    onHelpClick={() => onHelpClick('display')}
                  />
                )}
                {hasChartData(reportData.dooh_chart) && (
                  <ComboChart
                    data={reportData.dooh_chart}
                    title="DOOH Performance"
                    color={CHANNEL_COLORS.dooh}
                    onHelpClick={() => onHelpClick('dooh')}
                  />
                )}
                {hasChartData(reportData.streaming_tv_chart) && (
                  <ComboChart
                    data={reportData.streaming_tv_chart}
                    title="Streaming TV Performance"
                    color={CHANNEL_COLORS.streaming_tv}
                    onHelpClick={() => onHelpClick('streaming_tv')}
                    showClicks={false}
                  />
                )}
                {hasChartData(reportData.broadcast_tv_chart) && (
                  <ComboChart
                    data={reportData.broadcast_tv_chart}
                    title="Broadcast TV Performance"
                    color={CHANNEL_COLORS.broadcast_tv}
                    showClicks={false}
                    onHelpClick={() => onHelpClick('broadcast_tv')}
                  />
                )}
                {hasChartData(reportData.sport_signage_chart) && (
                  <ComboChart
                    data={reportData.sport_signage_chart}
                    title="Sport Signage Performance"
                    color={CHANNEL_COLORS.sport_signage}
                    showClicks={false}
                    onHelpClick={() => onHelpClick('sport_signage')}
                  />
                )}
                {hasChartData(reportData.streaming_audio_chart) && (
                  <ComboChart
                    data={reportData.streaming_audio_chart}
                    title="Streaming Audio Performance"
                    color={CHANNEL_COLORS.streaming_audio}
                    showClicks={false}
                    onHelpClick={() => onHelpClick('streaming_audio')}
                  />
                )}
                {hasChartData(reportData.youtube_chart) && (
                  <ComboChart
                    data={reportData.youtube_chart}
                    title="YouTube Performance"
                    color={CHANNEL_COLORS.youtube}
                    onHelpClick={() => onHelpClick('youtube')}
                  />
                )}
                {hasChartData(reportData.social_chart) && (
                  <ComboChart
                    data={reportData.social_chart}
                    title="Social Performance"
                    color={CHANNEL_COLORS.social}
                    onHelpClick={() => onHelpClick('social')}
                  />
                )}
                {hasChartData(reportData.paid_social_chart) && (
                  <ComboChart
                    data={reportData.paid_social_chart}
                    title="Paid Social Performance"
                    color={CHANNEL_COLORS.paid_social}
                    onHelpClick={() => onHelpClick('paid_social')}
                  />
                )}
                {hasChartData(reportData.store_visits_chart) && (
                  <ComboChart
                    data={reportData.store_visits_chart}
                    title="Store Visits Performance"
                    color={CHANNEL_COLORS.store_visits}
                    showClicks={false}
                    onHelpClick={() => onHelpClick('store_visits')}
                  />
                )}
              </>
            )}
          </div>
        </section>

        {/* Radio Table Section */}
        {hasTableData(reportData.radio) && (
          <section className="radio-section">
            {isLoading ? (
              <SkeletonTable columns={7} rows={5} />
            ) : (
              <RadioTable
                data={reportData.radio}
                onHelpClick={() => onHelpClick('radio')}
              />
            )}
          </section>
        )}

        {/* Television Table Section */}
        {hasTableData(reportData.television) && (
          <section className="television-section">
            {isLoading ? (
              <SkeletonTable columns={7} rows={5} />
            ) : (
              <TelevisionTable
                data={reportData.television}
                onHelpClick={() => onHelpClick('television')}
              />
            )}
          </section>
        )}

        {/* Sport Signage Table Section */}
        {hasTableData(reportData.sport_signage) && (
          <section className="sport-signage-section">
            {isLoading ? (
              <SkeletonTable columns={7} rows={5} />
            ) : (
              <SportSignageTable
                data={reportData.sport_signage}
                onHelpClick={() => onHelpClick('sport_signage')}
              />
            )}
          </section>
        )}

        {/* Map Section */}
        <section className="map-section">
          <div className="section-header">
            <h2 className="section-title">
              Advertising Reach
              <HelpIcon onClick={() => onHelpClick('map')} />
            </h2>
          </div>
          <MapView 
            data={reportData.map} 
            viewLevel="dealer"
            dealerLocation={{
              address: dealerInfo?.address,
              city: dealerInfo?.city,
              state: dealerInfo?.state,
              zip: dealerInfo?.zip
            }}
            dealerLocations={dealerLocations}
            selectedRadius={selectedRadius}
          />
        </section>
      </div>
    </div>
  );
};

export default ReportsView;